import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Trash2, 
  MapPin,
  DollarSign,
  Euro
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { deliveryZoneAPI } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export const DeliveryZonesPage = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const { restaurant } = useAuth();

  const fetchZones = async () => {
    try {
      const response = await deliveryZoneAPI.getAll();
      setZones(response.data);
    } catch (error) {
      toast.error('Kon bezorgzones niet ophalen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);

  const handleDelete = async (zoneId) => {
    if (!window.confirm('Deze bezorgzone verwijderen?')) return;
    try {
      await deliveryZoneAPI.delete(zoneId);
      toast.success('Zone verwijderd');
      fetchZones();
    } catch (error) {
      toast.error('Kon zone niet verwijderen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="delivery-zones-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Bezorgzones</h1>
          <p className="text-gray-500">Stel bezorgkosten en minimum bestelbedragen per postcode in</p>
        </div>
        <Button 
          className="btn-primary"
          onClick={() => { setEditingZone(null); setShowModal(true); }}
          data-testid="add-zone-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Zone Toevoegen
        </Button>
      </div>

      {/* Default Zone Info */}
      <div className="card-dashboard bg-[#D8F3DC]/30">
        <h3 className="font-medium text-gray-900 mb-2">Standaard bezorging</h3>
        <p className="text-sm text-gray-600">
          Voor postcodes zonder specifieke zone gelden de standaard instellingen:
        </p>
        <div className="mt-3 flex gap-6 text-sm">
          <span><strong>Bezorgkosten:</strong> {formatPrice(restaurant?.default_delivery_fee || 2.99, 'EUR')}</span>
          <span><strong>Minimum bestelling:</strong> {formatPrice(restaurant?.default_min_order_amount || 15.0, 'EUR')}</span>
        </div>
      </div>

      {/* Zones List */}
      {zones.length === 0 ? (
        <div className="card-dashboard text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">Geen bezorgzones</h3>
          <p className="text-gray-500 mb-4">Voeg postcodes toe met aangepaste bezorgkosten</p>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Eerste zone toevoegen
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {zones.map((zone) => (
            <div key={zone.id} className="card-dashboard" data-testid={`zone-card-${zone.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#D8F3DC] rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                  <div>
                    <p className="font-heading text-xl font-bold text-gray-900">{zone.postal_code}</p>
                    {zone.city && <p className="text-sm text-gray-500">{zone.city}</p>}
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${zone.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {zone.is_active ? 'Actief' : 'Inactief'}
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Bezorgkosten</span>
                  <span className="font-medium text-gray-900">{formatPrice(zone.delivery_fee, 'EUR')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Minimum bestelling</span>
                  <span className="font-medium text-gray-900">{formatPrice(zone.min_order_amount, 'EUR')}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1"
                  onClick={() => { setEditingZone(zone); setShowModal(true); }}
                >
                  Bewerken
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(zone.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zone Modal */}
      <ZoneModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingZone(null); }}
        zone={editingZone}
        onSave={fetchZones}
      />
    </div>
  );
};

const ZoneModal = ({ open, onClose, zone, onSave }) => {
  const [formData, setFormData] = useState({
    postal_code: '',
    city: '',
    delivery_fee: '',
    min_order_amount: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (zone) {
      setFormData({
        postal_code: zone.postal_code,
        city: zone.city || '',
        delivery_fee: zone.delivery_fee.toString(),
        min_order_amount: zone.min_order_amount.toString(),
      });
    } else {
      setFormData({
        postal_code: '',
        city: '',
        delivery_fee: '3.99',
        min_order_amount: '15.00',
      });
    }
  }, [zone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const data = {
        postal_code: formData.postal_code,
        city: formData.city || null,
        delivery_fee: parseFloat(formData.delivery_fee),
        min_order_amount: parseFloat(formData.min_order_amount),
      };
      
      if (zone) {
        await deliveryZoneAPI.update(zone.id, data);
        toast.success('Zone bijgewerkt');
      } else {
        await deliveryZoneAPI.create(data);
        toast.success('Zone toegevoegd');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Kon zone niet opslaan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{zone ? 'Zone Bewerken' : 'Nieuwe Zone'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="postal_code">Postcode *</Label>
              <Input
                id="postal_code"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                placeholder="1000"
                required
                data-testid="zone-postal-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">Stad</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Brussel"
                data-testid="zone-city-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="delivery_fee">Bezorgkosten (€) *</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="delivery_fee"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.delivery_fee}
                  onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
                  className="pl-9"
                  required
                  data-testid="zone-fee-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="min_order">Minimum bestelling (€) *</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="min_order"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                  className="pl-9"
                  required
                  data-testid="zone-min-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuleren</Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="save-zone-btn">
              {loading ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
